import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  AlertOctagon, 
  Activity, 
  Apple, 
  CloudLightning, 
  Wind, 
  Shield,
  ExternalLink,
  AlertTriangle,
  Thermometer,
  Droplets
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IconComponent = ({ iconName, className = "w-6 h-6" }) => {
  switch (iconName) {
    case 'AlertOctagon': return <AlertOctagon className={`${className} text-orange-400`} />;
    case 'Activity': return <Activity className={`${className} text-green-400`} />;
    case 'Apple': return <Apple className={`${className} text-lime-400`} />;
    case 'CloudLightning': return <CloudLightning className={`${className} text-amber-400`} />;
    case 'Wind': return <Wind className={`${className} text-cyan-400`} />;
    case 'Shield': return <Shield className={`${className} text-red-400`} />;
    default: return null;
  }
};

// Terrorism Advisories Content
const TerrorismContent = ({ data }) => {
  if (!data) return <NoDataMessage />;
  
  return (
    <div className="space-y-4">
      {/* NTAS Bulletin */}
      {data.ntas_bulletin && (
        <div className="p-4 rounded-lg bg-orange-950/30 border border-orange-800/30">
          <div className="flex items-start justify-between mb-3">
            <h4 className="font-semibold text-orange-300">{data.ntas_bulletin.title}</h4>
            <Badge className={`${
              data.threat_level === 'elevated' ? 'bg-orange-600' : 
              data.threat_level === 'imminent' ? 'bg-red-600' : 'bg-green-600'
            } text-white text-xs`}>
              {data.threat_level?.toUpperCase() || 'NORMAL'}
            </Badge>
          </div>
          <p className="text-sm text-slate-300 mb-3">{data.ntas_bulletin.summary}</p>
          
          {data.ntas_bulletin.key_points && (
            <div className="mb-3">
              <p className="text-xs text-slate-400 mb-2 font-medium">Key Points:</p>
              <ul className="text-xs text-slate-300 space-y-1 ml-4 list-disc">
                {data.ntas_bulletin.key_points.slice(0, 3).map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            </div>
          )}
          
          <p className="text-xs text-slate-500">
            Issued: {data.ntas_bulletin.issued_date} | Expires: {data.ntas_bulletin.expires_date}
          </p>
          {data.ntas_bulletin.source_url && (
            <a 
              href={data.ntas_bulletin.source_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-teal-400 hover:underline mt-2 inline-flex items-center gap-1"
            >
              View official DHS bulletin <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
      
      {/* Updates */}
      {data.updates && data.updates.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 mb-2 font-medium">Recent Threat Updates:</p>
          {data.updates.map((update, idx) => (
            <div key={idx} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 mb-2">
              <div className="flex items-start justify-between mb-1">
                <h5 className="text-sm font-medium text-slate-200">{update.title}</h5>
                <Badge className={`${
                  update.severity === 'high' ? 'bg-red-600' : 
                  update.severity === 'moderate' ? 'bg-yellow-600' : 'bg-green-600'
                } text-white text-[10px]`}>
                  {update.severity}
                </Badge>
              </div>
              <p className="text-xs text-slate-400">{update.summary}</p>
              <p className="text-[10px] text-slate-500 mt-1">{update.source} | {update.date}</p>
            </div>
          ))}
        </div>
      )}
      
      <p className="text-xs text-slate-500 text-center">Source: DHS National Terrorism Advisory System</p>
    </div>
  );
};

// Disease Outbreaks Content (CDC)
const DiseaseOutbreaksContent = ({ data }) => {
  if (!data?.outbreaks) return <NoDataMessage />;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
        <span>{data.outbreaks.length} active outbreak alerts</span>
        <span className="text-xs">Updated: {new Date(data.last_updated).toLocaleDateString()}</span>
      </div>
      
      {data.outbreaks.map((outbreak, idx) => (
        <div key={idx} className="p-4 rounded-lg bg-green-950/30 border border-green-800/30">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-green-300">{outbreak.title || outbreak.disease}</h4>
            <Badge className={`${
              outbreak.severity === 'elevated' ? 'bg-orange-600' : 'bg-green-600'
            } text-white text-xs`}>
              {outbreak.status?.toUpperCase() || 'ACTIVE'}
            </Badge>
          </div>
          <p className="text-sm text-slate-300 mb-2">{outbreak.description}</p>
          
          {outbreak.cases_reported && (
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
              <AlertTriangle className="w-3 h-3 text-amber-400" />
              <span>Cases: {outbreak.cases_reported}</span>
            </div>
          )}
          
          <p className="text-xs text-slate-400">Affected: {outbreak.affected_areas}</p>
          
          {outbreak.source_url && (
            <a 
              href={outbreak.source_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-teal-400 hover:underline mt-2 inline-flex items-center gap-1"
            >
              View CDC details <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      ))}
      
      <a 
        href={data.official_url || "https://www.cdc.gov/outbreaks/"} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-xs text-teal-400 hover:underline block text-center"
      >
        View all CDC outbreak information
      </a>
    </div>
  );
};

// Food Safety Recalls Content (FDA)
const FoodSafetyContent = ({ data }) => {
  if (!data?.recalls) return <NoDataMessage />;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
        <span>{data.total_active || data.recalls.length} active recalls</span>
        <Badge className={`${
          data.api_status === 'live' ? 'bg-green-600' : 'bg-yellow-600'
        } text-white text-[10px]`}>
          {data.api_status === 'live' ? 'LIVE DATA' : 'CACHED'}
        </Badge>
      </div>
      
      {data.recalls.slice(0, 5).map((recall, idx) => (
        <div key={idx} className="p-4 rounded-lg bg-lime-950/30 border border-lime-800/30">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-lime-300 text-sm line-clamp-2">
              {recall.product?.slice(0, 100)}{recall.product?.length > 100 ? '...' : ''}
            </h4>
            <Badge className={`${
              recall.severity?.includes('I') && !recall.severity?.includes('II') && !recall.severity?.includes('III') 
                ? 'bg-red-600' 
                : recall.severity?.includes('II') 
                  ? 'bg-orange-600' 
                  : 'bg-yellow-600'
            } text-white text-[10px] ml-2 whitespace-nowrap`}>
              {recall.severity?.split(' ')[0] || 'RECALL'}
            </Badge>
          </div>
          
          <div className="space-y-1 text-xs">
            <p className="text-slate-300">
              <span className="text-slate-500">Brand:</span> {recall.brand}
            </p>
            <p className="text-slate-300">
              <span className="text-slate-500">Reason:</span> {recall.reason}
            </p>
            {recall.distribution && (
              <p className="text-slate-400 line-clamp-2">
                <span className="text-slate-500">Distribution:</span> {recall.distribution}
              </p>
            )}
            {recall.recall_date && (
              <p className="text-slate-500 text-[10px]">
                Recall Date: {recall.recall_date}
              </p>
            )}
          </div>
        </div>
      ))}
      
      <a 
        href={data.official_url || "https://www.fda.gov/safety/recalls-market-withdrawals-safety-alerts"} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-xs text-teal-400 hover:underline block text-center"
      >
        View all FDA recalls
      </a>
    </div>
  );
};

// Weather Alerts Content (NWS)
const WeatherAlertsContent = ({ data }) => {
  if (!data?.alerts || data.alerts.length === 0) {
    return (
      <div className="text-center py-8">
        <CloudLightning className="w-12 h-12 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400">No active severe weather alerts</p>
        <p className="text-xs text-slate-500 mt-1">Check back later or visit weather.gov</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
        <span>{data.count || data.alerts.length} active alerts</span>
        <span className="text-xs">Source: National Weather Service</span>
      </div>
      
      {data.alerts.slice(0, 5).map((alert, idx) => (
        <div key={idx} className="p-4 rounded-lg bg-amber-950/30 border border-amber-800/30">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              {alert.event?.toLowerCase().includes('tornado') && <AlertTriangle className="w-4 h-4 text-red-400" />}
              {alert.event?.toLowerCase().includes('flood') && <Droplets className="w-4 h-4 text-blue-400" />}
              {alert.event?.toLowerCase().includes('heat') && <Thermometer className="w-4 h-4 text-orange-400" />}
              <h4 className="font-semibold text-amber-300">{alert.event}</h4>
            </div>
            <Badge className={`${
              alert.severity === 'Extreme' ? 'bg-red-600' : 
              alert.severity === 'Severe' ? 'bg-orange-600' : 
              alert.severity === 'Moderate' ? 'bg-yellow-600' : 'bg-blue-600'
            } text-white text-xs`}>
              {alert.severity}
            </Badge>
          </div>
          
          <p className="text-sm text-slate-300 mb-2">{alert.headline}</p>
          
          {alert.instruction && (
            <div className="p-2 rounded bg-slate-800/50 mb-2">
              <p className="text-xs text-slate-300">{alert.instruction.slice(0, 200)}{alert.instruction.length > 200 ? '...' : ''}</p>
            </div>
          )}
          
          <p className="text-xs text-slate-400 line-clamp-2">
            <span className="text-slate-500">Areas:</span> {alert.areaDesc || alert.areas_affected}
          </p>
          
          {alert.expires && (
            <p className="text-[10px] text-slate-500 mt-1">
              Expires: {new Date(alert.expires).toLocaleString()}
            </p>
          )}
        </div>
      ))}
      
      <a 
        href="https://www.weather.gov/alerts" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-xs text-teal-400 hover:underline block text-center"
      >
        View all NWS alerts
      </a>
    </div>
  );
};

// Air Quality Content (EPA)
const AirQualityContent = ({ data }) => {
  if (!data || data.aqi === undefined) return <NoDataMessage message="Air quality data not available for this location" />;
  
  const getAqiColor = (aqi) => {
    if (aqi <= 50) return '#00e400';
    if (aqi <= 100) return '#ffff00';
    if (aqi <= 150) return '#ff7e00';
    if (aqi <= 200) return '#ff0000';
    if (aqi <= 300) return '#8f3f97';
    return '#7e0023';
  };
  
  const getAqiTextColor = (aqi) => {
    if (aqi <= 100) return 'text-slate-900';
    return 'text-white';
  };
  
  return (
    <div className="space-y-4">
      {/* Main AQI Display */}
      <div 
        className="p-6 rounded-lg text-center border"
        style={{ 
          backgroundColor: `${data.color || getAqiColor(data.aqi)}20`, 
          borderColor: `${data.color || getAqiColor(data.aqi)}50` 
        }}
      >
        <div 
          className="text-5xl font-bold mb-2" 
          style={{ color: data.color || getAqiColor(data.aqi) }}
        >
          {data.aqi}
        </div>
        <div className="text-lg font-medium text-slate-200">{data.level_name || data.level?.replace('_', ' ').toUpperCase()}</div>
        <p className="text-sm text-slate-400 mt-2">{data.health_message}</p>
      </div>
      
      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="p-3 rounded-lg bg-slate-800/50">
          <p className="text-slate-500 text-xs">Dominant Pollutant</p>
          <p className="text-slate-200 font-medium">{data.dominant_pollutant || 'PM2.5'}</p>
        </div>
        <div className="p-3 rounded-lg bg-slate-800/50">
          <p className="text-slate-500 text-xs">Last Updated</p>
          <p className="text-slate-200 font-medium">
            {data.last_updated ? new Date(data.last_updated).toLocaleTimeString() : 'Recently'}
          </p>
        </div>
      </div>
      
      {/* Forecast */}
      {data.forecast && data.forecast.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 mb-2 font-medium">Forecast</p>
          <div className="flex gap-2">
            {data.forecast.map((day, idx) => (
              <div 
                key={idx} 
                className="flex-1 p-2 rounded-lg bg-slate-800/50 text-center"
              >
                <p className="text-xs text-slate-500">{day.day}</p>
                <p 
                  className="text-lg font-bold"
                  style={{ color: getAqiColor(day.aqi) }}
                >
                  {day.aqi}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {data.note && (
        <p className="text-xs text-slate-500 italic text-center">{data.note}</p>
      )}
      
      <a 
        href={data.official_url || "https://www.airnow.gov/"} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-xs text-teal-400 hover:underline block text-center"
      >
        Check AirNow.gov for local details
      </a>
    </div>
  );
};

// Sex Offenders Content
const SexOffendersContent = ({ data }) => {
  return (
    <div className="space-y-4">
      {/* Disclaimer */}
      <div className="p-3 rounded-lg bg-red-950/30 border border-red-800/30">
        <p className="text-xs text-red-300">
          {data?.disclaimer || "This data is sourced from public sex offender registries. Always verify critical information with official sources. Misuse of this information may be subject to criminal and civil penalties."}
        </p>
      </div>
      
      {/* Stats if available */}
      {data?.total_registered && (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-slate-800/50">
            <p className="text-slate-500 text-xs">Total Registered</p>
            <p className="text-slate-200 font-bold text-xl">{data.total_registered.toLocaleString()}</p>
          </div>
          {data.high_risk_count && (
            <div className="p-3 rounded-lg bg-red-950/30 border border-red-800/30">
              <p className="text-slate-500 text-xs">High Risk (Level 3)</p>
              <p className="text-red-400 font-bold text-xl">{data.high_risk_count.toLocaleString()}</p>
            </div>
          )}
        </div>
      )}
      
      <div className="p-4 rounded-lg bg-slate-800/50">
        <p className="text-sm text-slate-400">
          Enable this layer to view registered sex offenders on the map within your search area.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          Data source: {data?.data_source || data?.source || 'Public Sex Offender Registries via NSOPW'}
        </p>
      </div>
      
      <a 
        href={data?.official_registry || "https://www.nsopw.gov/"} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-xs text-teal-400 hover:underline block text-center"
      >
        Visit official NSOPW registry
      </a>
    </div>
  );
};

// No Data Message Component
const NoDataMessage = ({ message = "No data available" }) => (
  <div className="text-center py-8">
    <AlertTriangle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
    <p className="text-slate-400">{message}</p>
    <p className="text-xs text-slate-500 mt-1">Try refreshing or check back later</p>
  </div>
);

// Loading Spinner
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Main LayerDetailsDialog Component
const LayerDetailsDialog = ({ 
  isOpen, 
  onClose, 
  selectedLayer, 
  token,
  userLocation 
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    if (isOpen && selectedLayer) {
      fetchLayerDetails(selectedLayer.key);
    }
  }, [isOpen, selectedLayer]);
  
  const fetchLayerDetails = async (layerKey) => {
    setLoading(true);
    setData(null);
    
    try {
      let endpoint = '';
      switch(layerKey) {
        case 'terrorism':
          endpoint = '/api/layers/terrorism/current';
          break;
        case 'diseaseOutbreaks':
          endpoint = '/api/layers/health/outbreaks';
          break;
        case 'foodSafety':
          endpoint = '/api/layers/food/recalls';
          break;
        case 'weather':
          endpoint = '/api/layers/weather/severe';
          break;
        case 'sexOffenders':
          endpoint = `/api/layers/offenders/stats/${userLocation?.state || 'CA'}`;
          break;
        case 'airQuality':
          endpoint = `/api/layers/air/quality?lat=${userLocation?.lat || 34}&lng=${userLocation?.lng || -118}`;
          break;
        default:
          break;
      }
      
      if (endpoint) {
        const res = await fetch(`${API_URL}${endpoint}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const responseData = await res.json();
          setData(responseData);
        }
      }
    } catch (err) {
      console.error('Failed to fetch layer details:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const renderContent = () => {
    if (loading) return <LoadingSpinner />;
    
    switch (selectedLayer?.key) {
      case 'terrorism':
        return <TerrorismContent data={data} />;
      case 'diseaseOutbreaks':
        return <DiseaseOutbreaksContent data={data} />;
      case 'foodSafety':
        return <FoodSafetyContent data={data} />;
      case 'weather':
        return <WeatherAlertsContent data={data} />;
      case 'airQuality':
        return <AirQualityContent data={data} />;
      case 'sexOffenders':
        return <SexOffendersContent data={data} />;
      default:
        return <NoDataMessage />;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl glass-card border-slate-700 max-h-[80vh] overflow-y-auto"
        aria-describedby="layer-details-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            {selectedLayer?.icon && <IconComponent iconName={selectedLayer.icon} />}
            <span className="text-slate-100">{selectedLayer?.label}</span>
          </DialogTitle>
          <p id="layer-details-description" className="sr-only">
            Detailed information for {selectedLayer?.label} data layer
          </p>
        </DialogHeader>
        
        <div className="mt-4">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LayerDetailsDialog;
